import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Build,
  Clear as ClearIcon,
  Description,
  FactCheck,
  FlightTakeoff,
  Inventory2,
  PrecisionManufacturing,
  Refresh,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { ROLES } from '../../constants/roles';
import { getAircraftLabel } from '../sales/SalesEnquiryUtils';

const CAMO_CARD_SHADE_A = '#f3f6f9';
const CAMO_CARD_SHADE_B = '#f5f3f8';

const EMPTY_COUNTS = {
  tlbUnderReview: 0,
  tlbSubmitted: 0,
  maintenanceQmAck: 0,
  maintenanceCamoRework: 0,
  maintenancePlanningQueue: 0,
  ampDueSoon: 0,
  adsbOpen: 0,
  edrInbox: 0,
  mroComponentApprovals: 0
};

const CAMO_COMPONENT_APPROVER_ROLES = [
  ROLES.CA_MANAGER,
  ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER,
  ROLES.ACCOUNTABLE_MANAGER,
  ROLES.SUPER_ADMIN
];

const EMPTY_INVENTORY_STATS = {
  totalItems: 0,
  lowStockCount: 0,
  expiringCount: 0,
  accessible: true
};

const CamoTeamDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [recentTechnicalLogs, setRecentTechnicalLogs] = useState([]);
  const [recentCharters, setRecentCharters] = useState([]);
  const [approvingTripIds, setApprovingTripIds] = useState({});
  const [inventoryStats, setInventoryStats] = useState(EMPTY_INVENTORY_STATS);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [expiringItems, setExpiringItems] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [partSearchInput, setPartSearchInput] = useState('');
  const [partSearchResults, setPartSearchResults] = useState([]);
  const [partSearchLoading, setPartSearchLoading] = useState(false);
  const [partSearchError, setPartSearchError] = useState(null);
  const [pendingMroComponents, setPendingMroComponents] = useState([]);
  const [componentActionIds, setComponentActionIds] = useState({});
  const [rejectDialog, setRejectDialog] = useState({ open: false, component: null, remarks: '' });

  const canApproveMroComponents = CAMO_COMPONENT_APPROVER_ROLES.includes(user?.role);

  const camoReviewLoad = useMemo(
    () =>
      (counts.tlbUnderReview || 0) +
      (counts.tlbSubmitted || 0) +
      (counts.maintenanceQmAck || 0) +
      (counts.edrInbox || 0) +
      (counts.mroComponentApprovals || 0),
    [counts]
  );

  const planningFollowUpLoad = useMemo(
    () => (counts.maintenancePlanningQueue || 0) + (counts.ampDueSoon || 0) + (counts.adsbOpen || 0),
    [counts]
  );

  const totalOpenItems = camoReviewLoad + planningFollowUpLoad;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        tlbResult,
        maintenanceResult,
        edrInboxResult,
        ampResult,
        adsbResult,
        tripsResult,
        inventoryResult,
        lowStockResult,
        expiringResult,
        aircraftResult,
        mroComponentsResult
      ] = await Promise.allSettled([
        axios.get('/api/technical-log-book'),
        axios.get('/api/maintenance'),
        axios.get('/api/maintenance/edr/inbox'),
        axios.get('/api/amp-schedules'),
        axios.get('/api/adsb-service-bulletins'),
        axios.get('/api/sales/trips'),
        axios.get('/api/store/inventory', { params: { page: 1, pageSize: 1 } }),
        axios.get('/api/store/inventory/low-stock'),
        axios.get('/api/store/inventory/expiring'),
        axios.get('/api/aircraft'),
        canApproveMroComponents
          ? axios.get('/api/components/camo-pending-approvals')
          : Promise.resolve({ data: { components: [] } })
      ]);

      const technicalLogs = tlbResult.status === 'fulfilled' ? (tlbResult.value.data || []) : [];
      const maintenance = maintenanceResult.status === 'fulfilled' ? (maintenanceResult.value.data || []) : [];
      const edrInbox =
        edrInboxResult.status === 'fulfilled' && Array.isArray(edrInboxResult.value.data)
          ? edrInboxResult.value.data
          : [];
      const ampSchedules = ampResult.status === 'fulfilled' ? (ampResult.value.data || []) : [];
      const adsbBulletins = adsbResult.status === 'fulfilled' ? (adsbResult.value.data || []) : [];
      const trips = tripsResult.status === 'fulfilled' ? (tripsResult.value.data || []) : [];

      const tlbUnderReview = technicalLogs.filter((entry) => entry?.workflowStatus === 'UNDER_REVIEW').length;
      const tlbSubmitted = technicalLogs.filter((entry) => entry?.workflowStatus === 'SUBMITTED').length;

      const maintenanceQmAck = maintenance.filter(
        (wo) =>
          wo?.status === 'SCHEDULED' &&
          wo?.camoQmAcknowledged !== true &&
          !wo?.camoQmRejectedAt
      ).length;

      const maintenanceCamoRework = maintenance.filter(
        (wo) =>
          wo?.status === 'SCHEDULED' &&
          wo?.camoQmRejectedAt &&
          wo?.camoQmAcknowledged !== true
      ).length;

      const maintenancePlanningQueue = maintenance.filter(
        (wo) =>
          wo?.status === 'SCHEDULED' &&
          wo?.camoQmAcknowledged === true &&
          wo?.planningApproved !== true &&
          !wo?.planningRejectedAt
      ).length;

      const now = new Date();
      const sixtyDaysFromNow = new Date(now);
      sixtyDaysFromNow.setDate(now.getDate() + 60);

      const ampDueSoon = ampSchedules.filter((schedule) => {
        if (!schedule?.nextDueDate) return false;
        const dueDate = new Date(schedule.nextDueDate);
        return dueDate >= now && dueDate <= sixtyDaysFromNow;
      }).length;

      const adsbOpen = adsbBulletins.filter(
        (bulletin) => !['COMPLETED', 'CLOSED'].includes((bulletin?.status || '').toUpperCase())
      ).length;

      const mroPendingComponents = mroComponentsResult.status === 'fulfilled'
        && Array.isArray(mroComponentsResult.value.data?.components)
        ? mroComponentsResult.value.data.components
        : [];

      setPendingMroComponents(mroPendingComponents);
      setCounts({
        tlbUnderReview,
        tlbSubmitted,
        maintenanceQmAck,
        maintenanceCamoRework,
        maintenancePlanningQueue,
        ampDueSoon,
        adsbOpen,
        edrInbox: edrInbox.length,
        mroComponentApprovals: mroPendingComponents.length
      });
      const sortedTechnicalLogs = [...technicalLogs].sort((a, b) => {
        const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
      const sortedTrips = [...trips].sort((a, b) => {
        const aTime = new Date(a?.updatedAt || a?.createdAt || 0).getTime();
        const bTime = new Date(b?.updatedAt || b?.createdAt || 0).getTime();
        return bTime - aTime;
      });
      setRecentTechnicalLogs(sortedTechnicalLogs.slice(0, 10));
      setRecentCharters(sortedTrips);

      const inventoryAccessible =
        inventoryResult.status === 'fulfilled' &&
        lowStockResult.status !== 'rejected' &&
        expiringResult.status !== 'rejected';

      const inventoryPayload = inventoryResult.status === 'fulfilled' ? inventoryResult.value.data : null;
      const totalItems = inventoryPayload?.pagination?.total
        ?? (Array.isArray(inventoryPayload?.items) ? inventoryPayload.items.length : 0)
        ?? (Array.isArray(inventoryPayload) ? inventoryPayload.length : 0);

      const lowStock = lowStockResult.status === 'fulfilled' && Array.isArray(lowStockResult.value.data)
        ? lowStockResult.value.data
        : [];
      const expiring = expiringResult.status === 'fulfilled' && Array.isArray(expiringResult.value.data)
        ? expiringResult.value.data
        : [];

      setInventoryStats({
        totalItems: totalItems || 0,
        lowStockCount: lowStock.length,
        expiringCount: expiring.length,
        accessible: inventoryAccessible
      });
      setLowStockItems(lowStock.slice(0, 10));
      setExpiringItems(expiring.slice(0, 10));

      const aircraftData = aircraftResult.status === 'fulfilled' && Array.isArray(aircraftResult.value.data)
        ? aircraftResult.value.data
        : [];
      setAircraftList(aircraftData);

      setLastUpdated(new Date());
    } catch (error) {
      setCounts(EMPTY_COUNTS);
      setRecentTechnicalLogs([]);
      setRecentCharters([]);
      setInventoryStats(EMPTY_INVENTORY_STATS);
      setLowStockItems([]);
      setExpiringItems([]);
      setAircraftList([]);
      setPendingMroComponents([]);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.role]);

  useEffect(() => {
    const trimmed = partSearchInput.trim();
    if (!trimmed) {
      setPartSearchResults([]);
      setPartSearchError(null);
      setPartSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setPartSearchLoading(true);
    setPartSearchError(null);

    const timer = setTimeout(async () => {
      try {
        const response = await axios.get('/api/store/inventory', {
          params: { search: trimmed, page: 1, pageSize: 25 }
        });
        if (cancelled) return;
        const payload = response.data;
        const items = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
        setPartSearchResults(items);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) {
          setPartSearchError('You do not have access to inventory search.');
        } else {
          setPartSearchError('Failed to search inventory.');
        }
        setPartSearchResults([]);
      } finally {
        if (!cancelled) setPartSearchLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [partSearchInput]);

  const cards = [
    {
      key: 'tlbUnderReview',
      title: 'Tech Log Under Review',
      value: counts.tlbUnderReview,
      icon: <FactCheck color="primary" />,
      description: 'Technical log entries currently in CAMO review workflow.',
      actionLabel: 'Open Technical Log Book',
      path: '/technical-log-book'
    },
    {
      key: 'tlbSubmitted',
      title: 'Tech Log Submitted',
      value: counts.tlbSubmitted,
      icon: <AssignmentTurnedIn color="primary" />,
      description: 'Fresh submissions waiting to be picked up for review.',
      actionLabel: 'Open Technical Log Book',
      path: '/technical-log-book'
    },
    {
      key: 'maintenanceQmAck',
      title: 'CAMO QM Acknowledgment Queue',
      value: counts.maintenanceQmAck,
      icon: <Build color="warning" />,
      description: 'Scheduled work orders pending CAMO QM acknowledgment.',
      actionLabel: 'Open Maintenance',
      path: '/maintenance'
    },
    {
      key: 'maintenanceCamoRework',
      title: 'Work Orders — CAMO Rework',
      value: counts.maintenanceCamoRework,
      icon: <Build color="error" />,
      description: 'Work orders rejected by CAMO QM awaiting revision by CAMO Manager or Technical Officer.',
      actionLabel: 'Open Maintenance',
      path: '/maintenance'
    },
    {
      key: 'edrInbox',
      title: 'Engineering Defect Reports (EDR)',
      value: counts.edrInbox,
      icon: <Description color="primary" />,
      description: 'EDRs sent to CAMO from the MRO workbench.',
      actionLabel: 'Open EDR inbox',
      path: '/maintenance',
      state: { openEdrTab: true }
    },
    {
      key: 'mroComponentApprovals',
      title: 'MRO Component Changes',
      value: counts.mroComponentApprovals,
      icon: <PrecisionManufacturing color="warning" />,
      description: 'Component add/edit/replace submissions from the MRO workbench awaiting CAMO approval.',
      actionLabel: 'Review below',
      path: '/camo-dashboard'
    },
    {
      key: 'maintenancePlanningQueue',
      title: 'Planning Approval Queue',
      value: counts.maintenancePlanningQueue,
      icon: <Build color="warning" />,
      description: 'Acknowledged work orders waiting planning approval.',
      actionLabel: 'Open Planning',
      path: '/planning'
    },
    {
      key: 'ampDueSoon',
      title: 'AMP Schedules Due (60 Days)',
      value: counts.ampDueSoon,
      icon: <FlightTakeoff color="info" />,
      description: 'AMP schedules with due dates in the next 60 days.',
      actionLabel: 'Open AMP Schedules',
      path: '/amp-schedules'
    },
    {
      key: 'adsbOpen',
      title: 'Open AD/SB Bulletins',
      value: counts.adsbOpen,
      icon: <Build color="info" />,
      description: 'Active AD/SB bulletins requiring CAMO follow-up.',
      actionLabel: 'Open AD/SB Service Bulletin',
      path: '/adsb-service-bulletin'
    }
  ];

  const getTechnicalLogStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'UNDER_REVIEW':
        return 'info';
      case 'SUBMITTED':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCharterStatusColor = (status) => {
    switch (status) {
      case 'PENDING_OPS':
      case 'READY_FOR_BA':
        return 'warning';
      case 'CREW_ASSIGNMENT':
      case 'READY_FOR_ATC':
      case 'CLEARED':
        return 'info';
      case 'ASSIGNED':
      case 'CREW_ACCEPTED':
      case 'COMPLETED':
      case 'CHARTER_COMPLETED':
        return 'success';
      case 'CREW_REJECTED':
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const isCamoApprovalPending = (trip) =>
    Array.isArray(trip?.pendingApprovals) &&
    trip.pendingApprovals.some((approval) => approval?.role === ROLES.CA_MANAGER);

  const canApproveCamo = (trip) =>
    [ROLES.CA_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN].includes(user?.role) &&
    isCamoApprovalPending(trip);

  const formatUserName = (person) => {
    if (!person) return '—';
    const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
    return name || person.email || '—';
  };

  const handleApproveMroComponent = async (component) => {
    if (!component?._id || !canApproveMroComponents) return;
    try {
      setComponentActionIds((prev) => ({ ...prev, [component._id]: 'approve' }));
      await axios.post(`/api/components/${component._id}/approve`);
      showToast(`Component ${component.code || component.partNumber || ''} approved`, 'success');
      await fetchData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to approve component', 'error');
    } finally {
      setComponentActionIds((prev) => {
        const next = { ...prev };
        delete next[component._id];
        return next;
      });
    }
  };

  const handleRejectMroComponent = async () => {
    const component = rejectDialog.component;
    const remarks = rejectDialog.remarks.trim();
    if (!component?._id || !remarks) {
      showToast('Rejection remarks are required', 'error');
      return;
    }
    try {
      setComponentActionIds((prev) => ({ ...prev, [component._id]: 'reject' }));
      await axios.post(`/api/components/${component._id}/reject`, { remarks });
      showToast(`Component ${component.code || component.partNumber || ''} rejected`, 'success');
      setRejectDialog({ open: false, component: null, remarks: '' });
      await fetchData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reject component', 'error');
    } finally {
      setComponentActionIds((prev) => {
        const next = { ...prev };
        delete next[component._id];
        return next;
      });
    }
  };

  const handleCamoApprove = async (trip) => {
    if (!trip?._id || !canApproveCamo(trip)) return;
    try {
      setApprovingTripIds((prev) => ({ ...prev, [trip._id]: true }));
      await axios.post(`/api/sales/trips/${trip._id}/camo-approve`, {
        decision: 'APPROVED'
      });
      showToast(`CAMO approval recorded for charter ${trip.charterNumber || trip._id}`, 'success');
      await fetchData();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to submit CAMO approval', 'error');
    } finally {
      setApprovingTripIds((prev) => ({ ...prev, [trip._id]: false }));
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4">CAMO Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor CAMO review queues, planning bottlenecks, and upcoming compliance work.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap">
          <Typography variant="h6">CAMO workload overview</Typography>
          <Chip
            color={camoReviewLoad > 0 ? 'warning' : 'success'}
            label={`Review load: ${camoReviewLoad}`}
          />
          <Chip
            color={planningFollowUpLoad > 0 ? 'info' : 'default'}
            label={`Planning and compliance follow-up: ${planningFollowUpLoad}`}
          />
          <Chip
            color={totalOpenItems > 0 ? 'warning' : 'success'}
            label={`Total open items: ${totalOpenItems}`}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last updated:{' '}
          {lastUpdated
            ? new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
              }).format(lastUpdated)
            : 'Never'}
        </Typography>
      </Paper>

      <Grid container spacing={2}>
        {cards.map((card, index) => (
          <Grid item xs={12} md={6} lg={4} key={card.key}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: index % 2 === 0 ? CAMO_CARD_SHADE_A : CAMO_CARD_SHADE_B,
                boxShadow: 'none',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h6">{card.title}</Typography>
                  {card.icon}
                </Stack>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  {loading ? '...' : card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => navigate(card.path, card.state ? { state: card.state } : undefined)}
                >
                  {card.actionLabel}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {canApproveMroComponents && (
        <Paper sx={{ mt: 3, p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box>
              <Typography variant="h6">MRO component changes — pending CAMO approval</Typography>
              <Typography variant="body2" color="text.secondary">
                Review component add, edit, and replacement submissions from the MRO workbench.
              </Typography>
            </Box>
            <Chip
              label={`Pending: ${pendingMroComponents.length}`}
              size="small"
              color={pendingMroComponents.length > 0 ? 'warning' : 'success'}
              variant="outlined"
            />
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Part number</TableCell>
                  <TableCell>Serial</TableCell>
                  <TableCell>Aircraft</TableCell>
                  <TableCell>Work order</TableCell>
                  <TableCell>Submitted by</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingMroComponents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        {loading ? 'Loading...' : 'No MRO component changes awaiting approval.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingMroComponents.map((component) => {
                    const aircraftId = component.aircraftId?._id || component.aircraftId;
                    const actionBusy = Boolean(componentActionIds[component._id]);
                    return (
                      <TableRow key={component._id} hover>
                        <TableCell>{component.code || '—'}</TableCell>
                        <TableCell>{component.partNumber || component.partId?.partNumber || '—'}</TableCell>
                        <TableCell>{component.serialNumber || '—'}</TableCell>
                        <TableCell>
                          {component.aircraftId?.registration
                            || getAircraftLabel(aircraftId, aircraftList)
                            || '—'}
                        </TableCell>
                        <TableCell>
                          {component.submittedFromWorkOrderId?.workOrderNumber
                            || component.submittedFromWorkOrderId?.title
                            || component.workOrderNumber
                            || '—'}
                        </TableCell>
                        <TableCell>{formatUserName(component.submittedBy)}</TableCell>
                        <TableCell>
                          {component.submittedAt
                            ? new Date(component.submittedAt).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={actionBusy}
                              onClick={() => {
                                if (aircraftId) {
                                  window.open(
                                    `/aircraft-lifecycle?aircraftId=${aircraftId}&componentId=${component._id}`,
                                    '_blank'
                                  );
                                }
                              }}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              disabled={actionBusy}
                              onClick={() => handleApproveMroComponent(component)}
                            >
                              {componentActionIds[component._id] === 'approve' ? 'Approving...' : 'Approve'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={actionBusy}
                              onClick={() => setRejectDialog({ open: true, component, remarks: '' })}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Paper sx={{ mt: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Charters</Typography>
          <Chip
            label={`Total: ${recentCharters.length}`}
            size="small"
            color="info"
            variant="outlined"
          />
        </Stack>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Charter</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Aircraft</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Flight Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>CAMO Approval</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentCharters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No charters found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentCharters.map((trip) => (
                  <TableRow key={trip._id} hover>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {trip.charterNumber || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {trip.title || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {getAircraftLabel(trip.listAircraftId || trip.aircraftId, aircraftList)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      <Chip
                        label={trip.flightType || 'CHARTER'}
                        size="small"
                        variant="outlined"
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      <Chip
                        label={trip.status || 'UNKNOWN'}
                        color={getCharterStatusColor(trip.status)}
                        size="small"
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {isCamoApprovalPending(trip) ? (
                        canApproveCamo(trip) ? (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleCamoApprove(trip)}
                            disabled={Boolean(approvingTripIds[trip._id])}
                            sx={{ fontSize: '0.7rem', py: 0.25, minHeight: '24px' }}
                          >
                            {approvingTripIds[trip._id] ? 'Approving...' : 'Approve'}
                          </Button>
                        ) : (
                          <Chip
                            label="Pending"
                            color="warning"
                            size="small"
                            variant="outlined"
                            sx={{ height: '20px', fontSize: '0.7rem' }}
                          />
                        )
                      ) : (
                        <Chip
                          label="Approved"
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ height: '20px', fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {trip.createdBy
                        ? `${trip.createdBy.firstName || ''} ${trip.createdBy.lastName || ''}`.trim() || trip.createdBy.email || 'Unknown'
                        : 'Unknown'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {trip.updatedAt || trip.createdAt
                        ? new Intl.DateTimeFormat('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }).format(new Date(trip.updatedAt || trip.createdAt))
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Technical Log Queue</Typography>
          <Button size="small" variant="outlined" onClick={() => navigate('/technical-log-book')}>
            Open Technical Log Book
          </Button>
        </Stack>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Sheet Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Aircraft</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentTechnicalLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No technical log entries found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentTechnicalLogs.map((entry) => (
                  <TableRow
                    key={entry._id}
                    hover
                    onClick={() => navigate('/technical-log-book', { state: { openEntryId: entry._id } })}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {entry.sheetNumber || 'Not set'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {entry.aircraftId?.registration || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      <Chip
                        label={entry.workflowStatus || 'UNKNOWN'}
                        color={getTechnicalLogStatusColor(entry.workflowStatus)}
                        size="small"
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      {entry.updatedAt || entry.createdAt
                        ? new Intl.DateTimeFormat('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          }).format(new Date(entry.updatedAt || entry.createdAt))
                        : 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ mt: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Inventory2 color="action" />
            <Box>
              <Typography variant="h6">Inventory Parts Status</Typography>
              <Typography variant="caption" color="text.secondary">
                Read-only snapshot of store inventory health.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Total parts: ${inventoryStats.totalItems}`}
              color="default"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`Low stock: ${inventoryStats.lowStockCount}`}
              color={inventoryStats.lowStockCount > 0 ? 'warning' : 'success'}
              size="small"
            />
            <Chip
              label={`Expiring (30 days): ${inventoryStats.expiringCount}`}
              color={inventoryStats.expiringCount > 0 ? 'error' : 'success'}
              size="small"
            />
          </Stack>
        </Stack>

        {!inventoryStats.accessible ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Inventory data is not accessible for your role.
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search part number or description..."
                value={partSearchInput}
                onChange={(e) => setPartSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {partSearchLoading ? (
                        <CircularProgress size={16} />
                      ) : partSearchInput ? (
                        <IconButton
                          size="small"
                          aria-label="clear search"
                          onClick={() => setPartSearchInput('')}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </InputAdornment>
                  )
                }}
              />

              {partSearchInput.trim() && (
                <Box sx={{ mt: 1 }}>
                  {partSearchError ? (
                    <Typography variant="body2" color="error" sx={{ py: 1 }}>
                      {partSearchError}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        {partSearchLoading
                          ? 'Searching…'
                          : `${partSearchResults.length} result${partSearchResults.length === 1 ? '' : 's'}${partSearchResults.length === 25 ? ' (showing first 25)' : ''}`}
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, mt: 0.5 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Part #</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Description</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Status</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }} align="right">Qty</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }} align="right">Reorder</TableCell>
                              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Aircraft</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {partSearchResults.length === 0 && !partSearchLoading ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                    No matching parts found
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              partSearchResults.map((item) => (
                                <TableRow key={item._id} hover>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                    {item.partNumber || 'N/A'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                    {item.partName || item.description || '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                    {item.category || '—'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                    <Chip
                                      label={item.status || 'UNKNOWN'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: '20px', fontSize: '0.7rem' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }} align="right">
                                    {item.quantity ?? 0}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }} align="right">
                                    {item.reorderPoint ?? 0}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                                    {item.aircraftId?.registration
                                      || (item.aircraftScope === 'GEN' ? 'GEN' : '—')}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Low Stock (top 10)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Part #</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }} align="right">Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }} align="right">Reorder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lowStockItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            All parts above reorder point
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      lowStockItems.map((item) => (
                        <TableRow key={item._id} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {item.partNumber || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {item.partName || item.description || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }} align="right">
                            {item.quantity ?? 0}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }} align="right">
                            {item.reorderPoint ?? 0}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Expiring within 30 days (top 10)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Part #</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }} align="right">Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>Expires</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            No parts expiring soon
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      expiringItems.map((item) => (
                        <TableRow key={item._id} hover>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {item.partNumber || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {item.partName || item.description || '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }} align="right">
                            {item.quantity ?? 0}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem', py: 0.5 }}>
                            {item.expiryDate
                              ? new Intl.DateTimeFormat('en-US', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                }).format(new Date(item.expiryDate))
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          </>
        )}
      </Paper>

      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, component: null, remarks: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject MRO component change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Component: {rejectDialog.component?.code || '—'} | PN{' '}
            {rejectDialog.component?.partNumber || '—'}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Rejection remarks"
            value={rejectDialog.remarks}
            onChange={(e) => setRejectDialog((prev) => ({ ...prev, remarks: e.target.value }))}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, component: null, remarks: '' })}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectDialog.remarks.trim() || Boolean(componentActionIds[rejectDialog.component?._id])}
            onClick={handleRejectMroComponent}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CamoTeamDashboard;
